import axios from 'axios';

const API_BASE_URL = 'https://uatapi.ecommerce.ashtamtechnologies.com/api/';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 80000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ========================================
// TOKEN MANAGER
// ========================================
const TokenManager = {
  getToken: () => localStorage.getItem('authToken'),

  setToken: (token) => {
    const clean = token ? token.replace(/^Bearer\s+/i, '').trim() : token;
    localStorage.setItem('authToken', clean);
  },

  removeToken: () => {
    const cookiePrefs = localStorage.getItem('ortho_cookie_prefs');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userId');
    localStorage.removeItem('cartId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('roleId');
    localStorage.removeItem('isLoggedIn');
    if (cookiePrefs) localStorage.setItem('ortho_cookie_prefs', cookiePrefs);
  },

isTokenValid: () => {
  const token = TokenManager.getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() < payload.exp * 1000;
  } catch {
    return false;
  }
},

handleAuthError: () => {
  TokenManager.removeToken();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?session=expired';
  }
},
};

// ========================================
// INTERCEPTORS
// ========================================
api.interceptors.request.use(
  (config) => {
    let token = TokenManager.getToken();

    // 🔴 ADD THIS CHECK
    if (token && !TokenManager.isTokenValid()) {
      TokenManager.handleAuthError();
      return Promise.reject('Token expired');
    }

    if (token) {
      token = token.replace(/^Bearer\s+/i, '').trim();
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // ── Force logout if admin changed this user's role ─────────────────
    const userId = localStorage.getItem('userId');
    if (userId) {
      const forceLogoutKey = `force_logout_${userId}`;
      if (localStorage.getItem(forceLogoutKey) === 'true') {
        console.warn(`🔒 Force logout triggered for userId: ${userId}`);
        localStorage.removeItem(forceLogoutKey);
        TokenManager.handleAuthError();
        return;
      }
    }
    return response.data;
  },
  async (error) => {
   if (error.response?.status === 401) {
  TokenManager.handleAuthError();
}
    return Promise.reject(error);
  }
);

// ========================================
// ROLE CONSTANTS
// ========================================
export const ROLES = [
  { id: 1, roleName: 'Admin' },
  { id: 2, roleName: 'Customer' },
];

export const getRoleId = (roleName) => {
  const found = ROLES.find((r) => r.roleName === roleName);
  return found ? found.id : 2;
};

export const getRoleName = (roleId) => {
  const found = ROLES.find((r) => r.id === parseInt(roleId));
  return found ? found.roleName : 'Customer';
};

// ========================================
// HELPER — decode JWT
// ========================================
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

// ========================================
// API SERVICE
// ========================================
export const apiService = {

  // ----------------------------------------
  // AUTH
  // ----------------------------------------

  loginUser: async (credentials) => {
    const payload = {
      email: credentials.email,
      password: credentials.password,
    };

    try {
      const response = await api.post('/Auth/login', payload);

      const rawToken = response.token || response.accessToken;
      const token = rawToken ? rawToken.replace(/^Bearer\s+/i, '').trim() : null;

      if (token) {
        TokenManager.setToken(token);

        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }

        let userId = null;

        if (response.user) {
          localStorage.setItem('userData', JSON.stringify(response.user));
          userId = response.user.id || response.user.userId || response.user.ID;
        } else if (response.userId) {
          userId = response.userId;
        }

        if (!userId) {
          const decoded = decodeJWT(token);
          userId = decoded?.nameid || decoded?.sub || decoded?.userId || decoded?.id;
        }

        if (userId) {
          localStorage.setItem('userId', String(userId));

          try {
            const userRole = await apiService.getUserRole(userId);
            let roleId = null;

            if (typeof userRole === 'number') {
              roleId = userRole;
            } else if (userRole?.roleId !== undefined) {
              roleId = userRole.roleId;
            } else if (userRole?.RoleId !== undefined) {
              roleId = userRole.RoleId;
            } else if (userRole?.role_id !== undefined) {
              roleId = userRole.role_id;
            } else if (userRole?.id !== undefined) {
              roleId = userRole.id;
            }

            if (roleId !== null && roleId !== undefined) {
              const roleName = getRoleName(roleId);
              localStorage.setItem('userRole', roleName.toLowerCase());
              localStorage.setItem('roleId', String(roleId));
              response.roleId = parseInt(roleId);
              response.roleName = roleName.toLowerCase();
            } else {
              console.warn('⚠️ Could not extract roleId — defaulting to Customer');
              localStorage.setItem('userRole', 'user');
              localStorage.setItem('roleId', '2');
              response.roleId = 2;
              response.roleName = 'user';
            }
          } catch (roleError) {
            console.error('⚠️ Failed to fetch role — defaulting to Customer:', roleError);
            localStorage.setItem('userRole', 'user');
            localStorage.setItem('roleId', '2');
            response.roleId = 2;
            response.roleName = 'user';
          }

          if (response.roleId === 2) {
            try {
              const cartInfo = await apiService.getUserCart(userId);
              if (cartInfo?.cartId) {
                localStorage.setItem('cartId', String(cartInfo.cartId));
              } else {
                localStorage.removeItem('cartId');
              }
            } catch (cartError) {
              console.error('⚠️ Failed to fetch cart on login:', cartError);
            }
          }
        } else {
          console.warn('⚠️ UserId not found in login response');
        }
      }

      localStorage.setItem('isLoggedIn', 'true');
      return response;
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  },

  logoutUser: () => {
    TokenManager.removeToken();
    window.location.replace('/login');
  },

  // ─────────────────────────────────────────────────────────────────────
  // registerUser
  //
  // Step 1 → POST /User/Save           → create user
  // Step 2 → GET  /User/GetAll         → find real userId by email
  // Step 3 → POST /UserRole/Save       → { "roleId": 2, "userId": <id> }
  //
  // /UserRole/Save is called with a direct axios POST (no interceptor
  // dependency) so it never accidentally picks up a wrong roleId from
  // any other context. roleId is ALWAYS hardcoded to 2 here.
  // ─────────────────────────────────────────────────────────────────────
  registerUser: async (userData) => {

    // ── Step 1: Create the user ──────────────────────────────────────
    const savePayload = {
      id:          'string',
      name:        userData.fullName,
      gender:
        userData.gender === 'female' ? 'Female' :
        userData.gender === 'male'   ? 'Male'   :
        userData.gender,
      phoneNumber: userData.phoneNumber,
      email:       userData.email,
      password:    userData.password || 'string',
    };

    console.log('📤 POST /User/Save →', savePayload.email);
    const saveResponse = await api.post('/User/Save', savePayload);
    console.log('✅ /User/Save response:', JSON.stringify(saveResponse));

    // ── Step 2: Get the real numeric userId by email ─────────────────
    let numericUserId = null;

    try {
      console.log('📤 GET /User/GetAll → searching for:', userData.email);
      const allUsers = await api.get('/User/GetAll');

      let usersArray = [];
      if (Array.isArray(allUsers))             usersArray = allUsers;
      else if (Array.isArray(allUsers?.data))  usersArray = allUsers.data;
      else if (Array.isArray(allUsers?.users)) usersArray = allUsers.users;

      console.log(`🔍 Total users: ${usersArray.length}`);

      const matched = usersArray.find(
        (u) => u.email?.toLowerCase().trim() === userData.email?.toLowerCase().trim()
      );

      if (matched) {
        const rawId = matched.id ?? matched.userId ?? matched.ID ?? matched.Id;
        numericUserId = parseInt(rawId, 10);
        console.log('✅ Found user → rawId:', rawId, '→ numericUserId:', numericUserId);
      } else {
        console.warn('⚠️ User not found in GetAll by email:', userData.email);
      }
    } catch (getAllErr) {
      console.error('❌ GET /User/GetAll failed:', getAllErr.response?.status, getAllErr.message);
    }

    // ── Step 3: POST /UserRole/Save → { "roleId": 2, "userId": id } ──
    if (numericUserId && !isNaN(numericUserId) && numericUserId > 0) {

      // Payload EXACTLY as your API expects:
      // { "roleId": 2, "userId": 1044 }
      const rolePayload = {
        roleId: 2,              // ← always 2 (Customer), hardcoded
        userId: numericUserId,  // ← real numeric id from GetAll
      };

      console.log('📤 POST /UserRole/Save →', JSON.stringify(rolePayload));

      try {
        // Using axios directly with the base URL so there is zero chance
        // of the interceptor or any shared state corrupting the payload.
        const token = TokenManager.getToken();
        const roleResponse = await axios.post(
          `${API_BASE_URL}/UserRole/Save`,
          rolePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token.replace(/^Bearer\s+/i, '').trim()}` } : {}),
            },
            timeout: 30000,
          }
        );
        console.log('✅ /UserRole/Save success → roleId: 2, userId:', numericUserId, '| response:', JSON.stringify(roleResponse.data));
      } catch (roleErr) {
        // User IS registered even if role fails — don't throw, just log
        console.error(
          '❌ /UserRole/Save failed:',
          roleErr.response?.status,
          JSON.stringify(roleErr.response?.data),
          roleErr.message
        );
      }

    } else {
      console.error('❌ numericUserId is invalid — /UserRole/Save NOT called. Value was:', numericUserId);
    }

    return saveResponse;
  },

  forgotPassword: (email) => api.post('/Auth/forgot-password', { email }),

  forgotPasswordToken: (email) => api.post('/Auth/forgot-password-token', { email }),

  resetPassword: (resetData) =>
    api.post('/Auth/reset-password', {
      email:       resetData.email,
      token:       resetData.token,
      newPassword: resetData.newPassword,
    }),

  changePassword: (passwordData) => {
    if (!TokenManager.getToken()) {
      throw new Error('Authentication required. Please login again.');
    }
    return api.post('/Auth/change-password', {
      oldPassword: passwordData.oldPassword,
      newPassword: passwordData.newPassword,
    });
  },

  // ----------------------------------------
  // USER ROLE APIs
  // ----------------------------------------

  getUserRole: async (userId) => {
    try {
      const response = await api.get(`/UserRole/GetByUserId/${userId}`);
      if (Array.isArray(response) && response.length > 0) return response[0];
      if (response && typeof response === 'object') return response;
      if (typeof response === 'number') return { roleId: response };
      console.warn('⚠️ Unexpected getUserRole format:', response);
      return null;
    } catch (error) {
      console.error('❌ getUserRole error:', error.response?.data || error.message);
      return null;
    }
  },

  saveUserRole: async (userRoleData) => {
    const roleId = parseInt(userRoleData.roleId);
    const userId = parseInt(userRoleData.userId);

    if (isNaN(userId) || userId <= 0) {
      const msg = `saveUserRole: invalid userId "${userRoleData.userId}"`;
      console.error('❌', msg);
      throw new Error(msg);
    }
    if (isNaN(roleId) || roleId <= 0) {
      const msg = `saveUserRole: invalid roleId "${userRoleData.roleId}"`;
      console.error('❌', msg);
      throw new Error(msg);
    }

    const payload = { roleId, userId };
    console.log('📤 POST /UserRole/Save:', JSON.stringify(payload));

    try {
      const response = await api.post('/UserRole/Save', payload);
      console.log('✅ saveUserRole success:', JSON.stringify(response));
      return response;
    } catch (error) {
      console.error('❌ saveUserRole failed:', error.response?.status, JSON.stringify(error.response?.data));
      throw error;
    }
  },

  updateUserRole: async (userId, roleId) => {
    const parsedUserId = parseInt(userId);
    const parsedRoleId = parseInt(roleId);

    if (isNaN(parsedUserId) || parsedUserId <= 0) throw new Error(`updateUserRole: invalid userId "${userId}"`);
    if (isNaN(parsedRoleId) || parsedRoleId <= 0) throw new Error(`updateUserRole: invalid roleId "${roleId}"`);

    const payload = { roleId: parsedRoleId, userId: parsedUserId };

    try {
      const response = await api.put('/UserRole/Update', payload);
      return response;
    } catch (error) {
      console.error('❌ updateUserRole failed:', error.response?.data || error.message);
      throw error;
    }
  },

  // ----------------------------------------
  // USER APIs
  // ----------------------------------------

  getUserById: (userId) => api.get(`/User/GetById/${userId}`),

  getAllUsers: async () => {
    try {
      return await api.get('/User/GetAll');
    } catch (error) {
      console.error('Failed to fetch users:', error);
      throw error;
    }
  },

  saveUser: async (userData) => {
    const payload = {
      id:          userData.id || 'string',
      name:        userData.name,
      gender:      userData.gender,
      phoneNumber: userData.phoneNumber,
      email:       userData.email,
      password:    userData.password || 'string',
    };

    try {
      const response = await api.post('/User/Save', payload);
      const roleId = userData.roleId || 2;

      try {
        const allUsers = await api.get('/User/GetAll');
        let usersArray = Array.isArray(allUsers) ? allUsers : Array.isArray(allUsers?.data) ? allUsers.data : [];
        const savedUser = usersArray.find((u) => u.email?.toLowerCase() === userData.email?.toLowerCase());

        if (savedUser) {
          const rawId = savedUser.id ?? savedUser.userId ?? savedUser.ID ?? savedUser.Id;
          const numericUserId = parseInt(rawId, 10);
          if (!isNaN(numericUserId) && numericUserId > 0) {
            await apiService.saveUserRole({ userId: numericUserId, roleId });
          }
        }
      } catch (roleError) {
        console.warn('⚠️ Role assignment failed:', roleError.response?.data || roleError.message);
      }

      return response;
    } catch (error) {
      console.error('Failed to save user:', error);
      throw error;
    }
  },

  updateUser: async (userData) => {
    const userId = userData.id || localStorage.getItem('userId');
    const payload = {
      id:          userId ? String(userId) : 'string',
      name:        userData.name        || 'string',
      gender:      userData.gender      || 'Male',
      phoneNumber: userData.phoneNumber || 'string',
      email:       userData.email       || 'string',
      password:    userData.password    || 'string',
    };

    try {
      const response = await api.put('/User/Update', payload);
      if (userData.roleId) await apiService.updateUserRole(userId, userData.roleId);
      return response;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  },

  updateUserProfile: (userData) => {
    const userId = userData.id || localStorage.getItem('userId');
    return api.put('/User/Update', {
      id:          userId ? String(userId) : 'string',
      name:        userData.name        || 'string',
      gender:      userData.gender      || 'Male',
      phoneNumber: userData.phoneNumber || 'string',
      email:       userData.email       || 'string',
      password:    userData.password    || 'string',
    });
  },

  deleteUser: async (userId) => {
    try {
      return await api.delete(`/User/Delete/${userId}`);
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  },

  // ----------------------------------------
  // CART APIs
  // ----------------------------------------

  getUserCart: async (userId) => {
    try {
      const response = await api.get(`/Cart/GetByUserId/${userId}`);

      let cart = null;
      if (Array.isArray(response) && response.length > 0) {
        cart = response.find((c) => c.cartStatus === 1 || c.cartStatus === 0) || response[0];
      } else if (response && typeof response === 'object' && !Array.isArray(response)) {
        cart = response;
      } else if (response?.data) {
        cart = Array.isArray(response.data) ? response.data[0] : response.data;
      }

      if (!cart) return null;
      const cartId = cart.cartId || cart.id || cart.CartId || cart.ID;
      return { cartId, userId: cart.userId || userId, cartStatus: cart.cartStatus, fullCart: cart };
    } catch (error) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  getCartDetails: async (userId, cartId) => {
    try {
      const response = await api.get(`/Cart/GetCartDetails/get-cart-details/${userId}/${cartId}`);
      return response?.data || response || null;
    } catch (error) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  getProductForDisplay: async (productId) => {
    try {
      return await api.get(`/Product/GetProductDisplay/${productId}`);
    } catch {
      return null;
    }
  },

  getUserCartWithItems: async (userId) => {
    try {
      let cartId = localStorage.getItem('cartId');

      if (!cartId || cartId === 'null') {
        const cartInfo = await apiService.getUserCart(userId);
        if (!cartInfo?.cartId) {
          localStorage.removeItem('cartId');
          return [];
        }
        cartId = cartInfo.cartId;
        localStorage.setItem('cartId', String(cartId));
      }

      localStorage.setItem('userId', String(userId));
      const cartDetails = await apiService.getCartDetails(userId, cartId);
      if (!cartDetails) return [];

      let cartItems = [];
      if (Array.isArray(cartDetails.cartItems))  cartItems = cartDetails.cartItems;
      else if (Array.isArray(cartDetails.items)) cartItems = cartDetails.items;
      else if (Array.isArray(cartDetails))       cartItems = cartDetails;
      else if (cartDetails.cart?.cartItems)      cartItems = cartDetails.cart.cartItems;
      else if (Array.isArray(cartDetails.data))  cartItems = cartDetails.data;

      const itemsWithDetails = await Promise.all(
        cartItems.map(async (item) => {
          if (!item.product?.imageUrl) {
            const productDetails = await apiService.getProductForDisplay(item.productId);
            if (productDetails) return { ...item, product: { ...item.product, ...productDetails } };
          }
          return item;
        })
      );

      return itemsWithDetails;
    } catch (error) {
      console.error('❌ getUserCartWithItems failed:', error);
      throw error;
    }
  },

  addToCart: async (cartData) => {
    const userId = localStorage.getItem('userId');
    const payload = {
      id:         0,
      cartId:     0,
      productId:  parseInt(cartData.productId)    || 0,
      quantity:   parseInt(cartData.quantity)     || 1,
      unitPrice:  parseFloat(cartData.unitPrice)  || 0,
      totalPrice: parseFloat(cartData.totalPrice) || 0,
      userId:     parseInt(userId) || 0,
      cartStatus: 0,
    };

    try {
      const response = await api.post('/Cart/AddToCart', payload);
      const cartId = response?.cartId || response?.data?.cartId || response?.id;
      if (cartId) localStorage.setItem('cartId', String(cartId));
      return response;
    } catch (error) {
      console.error('❌ addToCart failed:', error);
      throw error;
    }
  },

  addCartItem: async (cartItemData) => {
    const cartId = localStorage.getItem('cartId');
    if (!cartId || cartId === 'null') throw new Error('No cart found.');
    return api.post('/CartItems', {
      cartId:    parseInt(cartId),
      productId: parseInt(cartItemData.productId) || 0,
      quantity:  parseInt(cartItemData.quantity)  || 1,
      unitPrice: parseFloat(cartItemData.unitPrice) || 0,
    });
  },

  updateCartItem: async (cartItemId, cartData) => {
    const cartId = localStorage.getItem('cartId');
    return api.put(`/CartItems/${cartItemId}`, {
      id:        parseInt(cartItemId),
      cartId:    parseInt(cartId) || parseInt(cartData.cartId) || 0,
      productId: parseInt(cartData.productId) || 0,
      quantity:  parseInt(cartData.quantity)  || 1,
      unitPrice: parseFloat(cartData.unitPrice) || 0,
    });
  },

  deleteCartItem: (cartItemId) => api.delete(`/CartItems/${cartItemId}`),
  clearCart:      (cartId)     => api.delete(`/Cart/${cartId}`),

  // ----------------------------------------
  // PRODUCT APIs
  // ----------------------------------------

  getAllEcommerceProducts:  () => api.get('/Product'),
  getEcommerceProductById:  (productId) => api.get(`/ECommerce/${productId}`),
  getAllProductsForDisplay:  () => api.get('/Product/GetAllProductDisplay?size=10000'),
  getAllProducts:            () => api.get('Product/All'),
  getProductById:           (productId) => api.get(`/Product/${productId}`),

  createEcommerceProduct: async (productData) => {
    const payload = {
      id: 0,
      productName:         productData.productName         || '',
      itemNo:              productData.itemNo              || '',
      inventoryProductId:  parseInt(productData.inventoryProductId)  || 0,
      tag:                 productData.tag                 || '',
      medicalCategory:     parseInt(productData.medicalCategory)     || 0,
      productType:         parseInt(productData.productType)         || 0,
      uom:                 parseInt(productData.uom)                 || 0,
      storageRequirements: parseInt(productData.storageRequirements) || 0,
      license: false, stateLicense: false, isVisiblePrice: false,
      createdDate: new Date().toISOString(), updatedDate: new Date().toISOString(),
      isPublished: false, isFeatured: false,
      shortDescription: productData.shortDescription || '',
      longDescription:  productData.longDescription  || '',
      seO_Title:        productData.seoTitle         || '',
      seO_Keywords:     productData.seoKeywords      || '',
      seO_Description:  productData.seoDescription   || '',
      createdOn:    new Date().toISOString(),
      status:       false,
      outOfStock:   Boolean(productData.outOfStock)        || false,
      hasVariants:  false,
      fixedPrice:   Boolean(productData.fixedPriceEnabled) || false,
      salePrice:    parseFloat(productData.salePrice)      || 0,
      isVisibleProduct: true,
      placeholder1: productData.productName  || '',
      placeholder2: productData.placeholder2 || '',
      placeholder3: productData.placeholder3 || '',
      placeholder4: productData.placeholder4 || '',
      isActive: true,
    };

    try {
      const response = await api.post('/Product', payload);
      let productId = typeof response === 'number' ? response : response?.id || response?.data?.id;
      return { id: productId, ...response };
    } catch (error) {
      console.error('createEcommerceProduct failed:', error);
      throw error;
    }
  },

  updateEcommerceProduct: (productId, productData) => {
    const fixedPriceEnabled = productData.fixedPriceEnabled !== undefined
      ? Boolean(productData.fixedPriceEnabled)
      : Boolean(productData.hasFixedPrice);
    const salePriceValue = parseFloat(productData.SalePrice || productData.salePrice || productData.placeholder2 || 0) || 0;

    return api.put('/Product', {
      id:                  productId,
      productName:         productData.productName         || '',
      itemNo:              productData.itemNo              || '',
      inventoryProductId:  parseInt(productData.inventoryProductId)  || 0,
      tag:                 productData.tag                 || '',
      medicalCategory:     parseInt(productData.medicalCategory)     || 0,
      productType:         parseInt(productData.productType)         || 0,
      uom:                 parseInt(productData.uom)                 || 0,
      storageRequirements: parseInt(productData.storageRequirements) || 0,
      license:        Boolean(productData.license),
      stateLicense:   Boolean(productData.stateLicense),
      isVisiblePrice: Boolean(productData.isVisiblePrice),
      createdDate:  productData.createdDate || new Date().toISOString(),
      updatedDate:  new Date().toISOString(),
      isPublished:  Boolean(productData.isPublished),
      isFeatured:   Boolean(productData.isFeatured),
      shortDescription: productData.shortDescription || '',
      longDescription:  productData.longDescription  || '',
      seO_Title:       productData.seO_Title    || productData.seoTitle    || '',
      seO_Keywords:    productData.seO_Keywords || productData.seoKeywords || '',
      seO_Description: productData.seO_Description || productData.seoDescription || '',
      createdOn:   productData.createdOn || new Date().toISOString(),
      status:      Boolean(productData.status),
      outOfStock:  Boolean(productData.outOfStock),
      hasVariants: Boolean(productData.hasVariants),
      fixedPrice:  fixedPriceEnabled,
      salePrice:   salePriceValue,
      isVisibleProduct: productData.isVisibleProduct !== undefined ? Boolean(productData.isVisibleProduct) : true,
      placeholder1: productData.productName  || '',
      placeholder2: productData.placeholder2 || '',
      placeholder3: productData.placeholder3 || '',
      placeholder4: productData.placeholder4 || '',
      isActive: productData.isActive !== undefined ? Boolean(productData.isActive) : true,
    });
  },

  deleteEcommerceProduct: (productId) => api.delete(`/Product/${productId}`),

  createProduct: (productData) => api.post('/Product', {
    id: 0,
    inventoryCode:    productData.inventoryCode    || 'string',
    brandId:          parseInt(productData.brandId)          || 0,
    productName:      productData.productName      || 'string',
    shortDescription: productData.shortDescription || 'string',
    longDescription:  productData.longDescription  || 'string',
    categoryName:     productData.categoryName     || 'string',
    subCategoryName:  productData.subCategoryName  || 'string',
    hsnsacCode:       productData.hsnsacCode       || 'string',
    unitId:           parseInt(productData.unitId)           || 0,
    gstId:            parseInt(productData.gstId)            || 0,
    gstPercentage:    parseFloat(productData.gstPercentage)  || 0,
    cessPercentage:   parseFloat(productData.cessPercentage) || 0,
    productDiscount:  parseFloat(productData.productDiscount)|| 0,
    reorderQuantity:  parseInt(productData.reorderQuantity)  || 0,
    manufacturerName: productData.manufacturerName || 'string',
    mrp:              parseFloat(productData.mrp)            || 0,
    purchasePrice:    parseFloat(productData.purchasePrice)  || 0,
    salesPrice:       parseFloat(productData.salesPrice)     || 0,
    openingStock:     parseInt(productData.openingStock)     || 0,
    godownId:         parseInt(productData.godownId)         || 0,
    storeId:          parseInt(productData.storeId)          || 0,
    expiryDate:       productData.expiryDate || new Date().toISOString(),
    description:      productData.description || 'string',
    isDeleted:        Boolean(productData.isDeleted) || false,
    createdBy:        productData.createdBy  || 'string',
    updatedBy:        productData.updatedBy  || 'string',
    deletedBy:        productData.deletedBy  || 'string',
    updatedDate:      productData.updatedDate || new Date().toISOString(),
    createdDate:      productData.createdDate || new Date().toISOString(),
  }),

  updateProduct: (productId, productData) => api.put('/Product', {
    id:               productId,
    inventoryCode:    productData.inventoryCode    || 'string',
    brandId:          parseInt(productData.brandId)          || 0,
    productName:      productData.productName      || 'string',
    shortDescription: productData.shortDescription || 'string',
    longDescription:  productData.longDescription  || 'string',
    categoryName:     productData.categoryName     || 'string',
    subCategoryName:  productData.subCategoryName  || 'string',
    hsnsacCode:       productData.hsnsacCode       || 'string',
    unitId:           parseInt(productData.unitId)           || 0,
    gstId:            parseInt(productData.gstId)            || 0,
    gstPercentage:    parseFloat(productData.gstPercentage)  || 0,
    cessPercentage:   parseFloat(productData.cessPercentage) || 0,
    productDiscount:  parseFloat(productData.productDiscount)|| 0,
    reorderQuantity:  parseInt(productData.reorderQuantity)  || 0,
    manufacturerName: productData.manufacturerName || 'string',
    mrp:              parseFloat(productData.mrp)            || 0,
    purchasePrice:    parseFloat(productData.purchasePrice)  || 0,
    salesPrice:       parseFloat(productData.salesPrice)     || 0,
    openingStock:     parseInt(productData.openingStock)     || 0,
    godownId:         parseInt(productData.godownId)         || 0,
    storeId:          parseInt(productData.storeId)          || 0,
    expiryDate:       productData.expiryDate || new Date().toISOString(),
    description:      productData.description || 'string',
    isDeleted:        Boolean(productData.isDeleted),
    createdBy:        productData.createdBy  || 'string',
    updatedBy:        productData.updatedBy  || 'string',
    deletedBy:        productData.deletedBy  || 'string',
    updatedDate:      new Date().toISOString(),
    createdDate:      productData.createdDate || new Date().toISOString(),
  }),

  deleteProduct: (productId) => api.delete(`/Product/${productId}`),

  // ----------------------------------------
  // PRODUCT IMAGE APIs
  // ----------------------------------------

  uploadProductImage: async (imageData) => {
    const formData = new FormData();
    formData.append('id', imageData.id);
    formData.append('image', imageData.imageFile);
    formData.append('isPrimary', imageData.isPrimary || false);

    const token = TokenManager.getToken();
    const imageApi = axios.create({ baseURL: API_BASE_URL, timeout: 80000, headers: { 'Content-Type': 'multipart/form-data' } });
    if (token) imageApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return imageApi.post('/ProductImage', formData);
  },

  uploadMultipleProductImages: async (productId, imageFiles, shouldSetPrimaryOnFirst = false) => {
    const validProductId = parseInt(productId);
    if (isNaN(validProductId) || validProductId === 0) throw new Error(`Invalid product ID: ${productId}`);

    const token = TokenManager.getToken();
    const imageApi = axios.create({ baseURL: API_BASE_URL, timeout: 80000, headers: { 'Content-Type': 'multipart/form-data' } });
    if (token) imageApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const uploadedImages = [], errors = [];

    for (let i = 0; i < imageFiles.length; i++) {
      try {
        const formData = new FormData();
        formData.append('id', String(validProductId));
        formData.append('image', imageFiles[i]);
        formData.append('isPrimary', String(shouldSetPrimaryOnFirst && i === 0));
        const response = await imageApi.post('/ProductImage', formData);
        uploadedImages.push(response.data);
      } catch (error) {
        errors.push({ index: i, error: error.message });
      }
    }

    return { success: uploadedImages, errors, total: imageFiles.length, uploaded: uploadedImages.length };
  },

  bulkUploadProductImages: async (productId, imageFiles, primaryImageIndex = -1) => {
    const validProductId = parseInt(productId);
    if (isNaN(validProductId) || validProductId === 0) throw new Error(`Invalid product ID: ${productId}`);

    const formData = new FormData();
    formData.append('ProductId', String(validProductId));
    imageFiles.forEach((f) => formData.append('ImageFiles', f));
    if (primaryImageIndex >= 0) formData.append('PrimaryImageIndex', String(primaryImageIndex));

    const token = TokenManager.getToken();
    const imageApi = axios.create({ baseURL: API_BASE_URL, timeout: 120000, headers: { 'Content-Type': 'multipart/form-data' } });
    if (token) imageApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    try {
      return await imageApi.post('/ProductImage/bulk-upload', formData);
    } catch (error) {
      console.error('Bulk upload failed:', error.response?.data || error.message);
      throw error;
    }
  },

  getProductImages: async (productId) => {
    const validProductId = parseInt(productId);
    if (isNaN(validProductId) || validProductId === 0) return [];
    try {
      return await api.get(`/ProductImage/product/${validProductId}`);
    } catch (error) {
      if (error.response?.status === 404) return [];
      throw error;
    }
  },

  deleteProductImage: (imageId) => api.delete(`/ProductImage/${imageId}`),

  reorderProductImages: async (productId, reorderedImages, primaryImageId) => {
    const validProductId = parseInt(productId);
    if (isNaN(validProductId) || validProductId === 0) throw new Error(`Invalid product ID: ${productId}`);

    const payload = reorderedImages.map((image, index) => ({ imageId: image.id, displayOrder: index + 1 }));

    let response;
    try {
      response = await api.put(`/ProductImage/reorder?productId=${validProductId}`, payload);
    } catch (putError) {
      if (putError.response?.status === 405) {
        response = await api.post(`/ProductImage/reorder?productId=${validProductId}`, payload);
      } else throw putError;
    }

    if (primaryImageId) {
      const primaryImage = reorderedImages.find((img) => img.id === primaryImageId);
      if (primaryImage && !primaryImage.isPrimary) {
        try {
          await api.put(`/ProductImage/${primaryImageId}/primary`, { isPrimary: true, productId: validProductId });
        } catch { /* endpoint may not exist */ }
      }
    }

    return response;
  },

  // ----------------------------------------
  // ORDER APIs
  // ----------------------------------------

  createOrder: async (orderData) => {
    const userId = localStorage.getItem('userId');
    let token    = localStorage.getItem('authToken');

    if (!token) throw new Error('Not authenticated — please login again.');
    token = token.replace(/^Bearer\s+/i, '').trim();

    const payload = {
      userId:            parseInt(userId) || parseInt(orderData.userId) || 0,
      subTotal:          parseFloat(orderData.subTotal)       || 0,
      taxAmount:         parseFloat(orderData.taxAmount)      || 0,
      shippingAmount:    parseFloat(orderData.shippingAmount) || 0,
      discountAmount:    parseFloat(orderData.discountAmount) || 0,
      billingAddressId:  parseInt(orderData.billingAddressId)  || 0,
      shippingAddressId: parseInt(orderData.shippingAddressId) || 0,
      placeholder1: 'string',
      placeholder2: 'string',
      placeholder3: 'string',
      placeholder4: 'string',
      paymentMethod: parseInt(orderData.paymentMethod) || 0,
      remarks:       orderData.remarks || 'string',
    };

    try {
      const response = await api.post('/Orders', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('❌ createOrder failed:', error.response?.status, JSON.stringify(error.response?.data));
      throw error;
    }
  },

  createOrderItem: async (orderItemData) => {
    const payload = {
      orderId:     parseInt(orderItemData.orderId)     || 0,
      productId:   parseInt(orderItemData.productId)   || 0,
      productName: orderItemData.productName || 'string',
      sku:         orderItemData.sku         || 'string',
      quantity:    parseInt(orderItemData.quantity)    || 1,
      unitPrice:   parseFloat(orderItemData.unitPrice) || 0,
    };

    try {
      const response = await api.post('/OrderItems', payload);
      return response;
    } catch (error) {
      console.error('❌ createOrderItem failed:', error);
      throw error;
    }
  },

  getUserOrders: async (userId) => {
    try {
      const response = await api.get(`/Orders/by-user/${userId}`);
      return Array.isArray(response) ? response : [];
    } catch (error) {
      if (error.response?.status === 404) return [];
      throw error;
    }
  },

  getOrderItems: async (orderId, userId) => {
    try {
      const response = await api.get(`/OrderItems/by-order/${orderId}/user/${userId}`);
      return Array.isArray(response) ? response : [];
    } catch (error) {
      if (error.response?.status === 404) return [];
      throw error;
    }
  },

  // ----------------------------------------
  // ADDRESS APIs
  // ----------------------------------------

  getUserAddresses: async (userId) => {
    try {
      const response = await api.get(`/UserAddresses/user/${userId}`);
      return Array.isArray(response) ? response : [];
    } catch (error) {
      if (error.response?.status === 404) return [];
      console.error('❌ getUserAddresses failed:', error);
      throw error;
    }
  },

  createAddress: async (addressData) => {
    const userId = localStorage.getItem('userId');
    if (!userId) throw new Error('User not logged in');

    const payload = {
      userId:            parseInt(userId) || 0,
      fullName:          addressData.fullName          || '',
      mobileNo:          addressData.mobileNo          || '',
      alternateMobileNo: addressData.alternateMobileNo || '',
      email:             addressData.email             || '',
      addressLine1:      addressData.addressLine1      || '',
      addressLine2:      addressData.addressLine2      || '',
      isDefault:         Boolean(addressData.isDefault),
      isBilling:         Boolean(addressData.isBilling),
      isShipping:        Boolean(addressData.isShipping),
      landmark:          addressData.landmark          || '',
      city:              addressData.city              || '',
      state:             addressData.state             || '',
      country:           addressData.country           || '',
      pincode:           addressData.pincode           || '',
    };

    try {
      const response = await api.post('/UserAddresses', payload);
      return response;
    } catch (error) {
      console.error('❌ createAddress failed:', error.response?.data || error.message);
      throw error;
    }
  },

  updateAddress: async (addressId, addressData) => {
    const userId = localStorage.getItem('userId');
    if (!userId) throw new Error('User not logged in');

    const payload = {
      userId:            parseInt(userId) || 0,
      fullName:          addressData.fullName          || '',
      mobileNo:          addressData.mobileNo          || '',
      alternateMobileNo: addressData.alternateMobileNo || '',
      email:             addressData.email             || '',
      addressLine1:      addressData.addressLine1      || '',
      addressLine2:      addressData.addressLine2      || '',
      isDefault:         Boolean(addressData.isDefault),
      isBilling:         Boolean(addressData.isBilling),
      isShipping:        Boolean(addressData.isShipping),
      landmark:          addressData.landmark          || '',
      city:              addressData.city              || '',
      state:             addressData.state             || '',
      country:           addressData.country           || '',
      pincode:           addressData.pincode           || '',
    };

    try {
      const response = await api.put(`/UserAddresses/${addressId}`, payload);
      return response;
    } catch (error) {
      console.error('❌ updateAddress failed:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteAddress: async (addressId) => {
    try {
      const response = await api.delete(`/UserAddresses/${addressId}`);
      return response;
    } catch (error) {
      console.error('❌ deleteAddress failed:', error.response?.data || error.message);
      throw error;
    }
  },

  // ----------------------------------------
  // AUTH UTILS
  // ----------------------------------------

  isAuthenticated: () => !!TokenManager.getToken(),

  checkAuthStatus: () => {
    const isValid = TokenManager.isTokenValid();
    if (!isValid && TokenManager.getToken()) TokenManager.handleAuthError();
    return isValid;
  },

  // ----------------------------------------
  // CONTACT APIs
  // ----------------------------------------

  sendContactForm: async (contactData) => {
    const payload = {
      name:        contactData.name,
      phoneNumber: contactData.phoneNumber,
      email:       contactData.email,
      subject:     contactData.subject,
      message:     contactData.message,
    };

    try {
      const response = await api.post('/Contact/send', payload);
      return response;
    } catch (error) {
      console.error('❌ sendContactForm failed:', error);
      throw error;
    }
  },

  // ----------------------------------------
  // RAW HTTP METHODS
  // ----------------------------------------
  get:    (url, config)       => api.get(url, config),
  post:   (url, data, config) => api.post(url, data, config),
  put:    (url, data, config) => api.put(url, data, config),
  delete: (url, config)       => api.delete(url, config),
  patch:  (url, data, config) => api.patch(url, data, config),
};

export { TokenManager };
export default api;